import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import axios from "axios";
import moment from "moment";
import { InputMask } from "primereact/inputmask";
import Layout from "../components/Layout";
import CustomButton from "../components/Button";
import Loader from "../components/Loader";
import SignatureModal from "../components/SignatureModal";
import { cpSignAllEnabled, serverBaseUrl } from "../config/envs";
import { USA_STATE_LIST } from "../constant/constant";
import { showErrorToast } from "../utils/toast";
import { buildSignSubmit } from "./operatorSignSubmit";
import { lockedStampValues, pdfBuildErrorMessage } from "./pdfErrorMessage";
import SignAllPreview, { isWideScreen } from "./SignAllPreview";
import { labelByState, parseSignAllHash, prepareAgreements, splitBlocked, withHeld } from "./signAllBatch";
import {
  blockedRowText,
  blockedText,
  consentText,
  continueText,
  joinWords,
  missingSteps,
  progressText,
  resultsText,
  rowStatus,
  signButtonLabel,
  signedSummary,
  successHeading,
  unavailableText,
} from "./signAllCopy";
import { batchDetailsValid, detailGroups, typedFor } from "./signAllFields";
import { buildCpPdf } from "./signAllPdf";
import { failedStep, failureLine, logFailure, signInOrder } from "./signAllRun";
import {
  clearHandoffHash,
  fetchPdfOnce,
  loadBatch,
  loadLink,
  refreshForToday,
  secretHeader,
  signAfterPreflight,
  sortPreviewFailure,
  uploadSignature,
} from "./signAllSteps";
import { closesOnSubmit } from "./signTabClose";

const TITLE = {
  collabPhys: "Review and sign your supervisory agreements",
  collabOperator: "Review and sign your collaborative agreements",
};
const TOGETHER = "These agreements can't be signed together. Sign them one at a time from your portal.";
const REFUSAL = {
  disabled: "This page isn't available.",
  "nothing-to-sign": "There's nothing to sign here. Open your portal and try again.",
  "all-signed": "Everything here is already signed.",
  "wrong-type": TOGETHER,
  "mixed-signer": TOGETHER,
  "load-failed": "Your agreements couldn't be loaded. Open your portal and try again.",
};
const MASK = { date: ["99/99/9999", "MM/DD/YYYY"], phone: ["(999) 999-9999", "_"] };

const load = (secret) => loadLink(axios.get, serverBaseUrl, secret);

// The history entry pushed under the phone overlay, so Back closes the overlay.
const onPreviewEntry = () => window.history.state?.signAllPreview === true;

async function uploadFile(path, secret, file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${serverBaseUrl}${path}`, formData, {
    headers: { "Content-Type": "multipart/form-data", ...secretHeader(secret) },
  });
  const url = response?.data?.data?.url;
  // The response goes with the error, so its log line gives the status the server answered with.
  if (!url) throw Object.assign(new Error("upload returned no url"), { response });
  return url;
}

// Runs one signing step and replaces whatever it throws with the reason the signer sees, keeping the cause for the log line.
async function step(name, reason, work) {
  try {
    return await work();
  } catch (cause) {
    throw failedStep(name, reason, cause);
  }
}

// Reuses operatorSign.jsx's CP-page translation so a pdf-lib failure reads the same way on both pages.
const buildErrorFor = (agreement, error) =>
  pdfBuildErrorMessage(error, lockedStampValues({ isCpPage: true, cpFields: agreement.cpFields }));

const DetailInput = ({ id, input, value, disabled, onChange, dataDetail }) => {
  const className = "w-full rounded border border-gray-300 px-2 py-1 text-sm";
  if (MASK[input]) {
    const [mask, slotChar] = MASK[input];
    return (
      <InputMask
        id={id}
        data-detail={dataDetail}
        className={className}
        value={value}
        mask={mask}
        slotChar={slotChar}
        placeholder={input === "date" ? "MM/DD/YYYY" : "(999) 999-9999"}
        inputMode="numeric"
        disabled={disabled}
        onChange={(e) => onChange(e.target.value ?? "")}
      />
    );
  }
  if (input === "textarea") {
    return (
      <textarea
        id={id}
        data-detail={dataDetail}
        rows={3}
        className={className}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (input === "gender") {
    return (
      <select id={id} data-detail={dataDetail} className={className} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">Gender</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="Other">Prefer not to say</option>
      </select>
    );
  }
  return (
    <input
      id={id}
      data-detail={dataDetail}
      type={input}
      className={className}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const CheckIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-14 w-14 text-green-600">
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <path d="M7 12.5l3.5 3.5L17 9.5" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Z1: the small warning triangle shown inline in a blocked row, next to blockedRowText's reason.
const WarningIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-amber-600">
    <path d="M12 3.2 22.5 21H1.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M12 9.5v5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17.4" r="1" fill="currentColor" />
  </svg>
);

const SignAll = ({ mode }) => {
  // Read once: the effect below clears the hash, and StrictMode runs effects twice in development.
  const [secrets] = useState(() => parseSignAllHash(window.location.hash));
  const [phase, setPhase] = useState("loading");
  const [refusal, setRefusal] = useState(null);
  const [prepared, setPrepared] = useState({ eligible: [], date: null, agreements: [] });
  const [alreadySignedLabels, setAlreadySignedLabels] = useState([]);
  const [unavailableCount, setUnavailableCount] = useState(0);
  const [values, setValues] = useState({});
  const [opened, setOpened] = useState({});
  const [openSecret, setOpenSecret] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pageCounts, setPageCounts] = useState({});
  const [signImageUrl, setSignImageUrl] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [results, setResults] = useState({});
  const [buildErrors, setBuildErrors] = useState({});
  const [preflighting, setPreflighting] = useState(false);
  const [held, setHeld] = useState({});
  const pdfCache = useRef(new Map());
  const topRef = useRef(null);
  const rowRefs = useRef({});
  const shownSecret = useRef(null);
  const leavingPreview = useRef(false);
  const agreements = useMemo(() => withHeld(prepared.agreements, held), [prepared.agreements, held]);
  const { signable, blockedMissing, blockedUnavailable, blockedDetails, held: heldGroups } = useMemo(() => splitBlocked(agreements), [agreements]);

  useEffect(() => {
    clearHandoffHash(window.location, window.history);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const batch = await loadBatch({ enabled: cpSignAllEnabled, secrets, load, mode });
        if (cancelled) return;
        setAlreadySignedLabels(labelByState(batch.alreadySigned, USA_STATE_LIST).map((item) => item.label));
        setUnavailableCount(batch.unavailable.length);
        if (batch.refusal) {
          setRefusal(batch.refusal);
          setPhase("refused");
          return;
        }
        const date = moment().format("MM/DD/YYYY");
        setPrepared({ eligible: batch.agreements, date, agreements: prepareAgreements(batch.agreements, mode, date, USA_STATE_LIST) });
        setPhase("review");
      } catch (error) {
        // An unexpected throw would otherwise leave the loader spinning for good.
        if (cancelled) return;
        // A throw here is a bug rather than a failed agreement, so Sentry gets the exception itself (scrubEvent runs on it), not a warning line.
        console.warn(failureLine("load", "the batch", error));
        Sentry.captureException(error);
        setRefusal("load-failed");
        setPhase("refused");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [secrets, mode]);

  const groups = useMemo(() => detailGroups(signable), [signable]);
  const detailsValid = useMemo(() => batchDetailsValid(signable, groups, values), [signable, groups, values]);

  const basePdf = useCallback(
    (agreement) => fetchPdfOnce(pdfCache.current, fetch, agreement.secret, agreement.signLink.created_s3_url),
    []
  );

  const pdfFor = useCallback(
    async (agreement) =>
      buildCpPdf({
        pdfBytes: await basePdf(agreement),
        signImageUrl,
        signatureFields: agreement.signatureFields,
        cpFields: agreement.cpFields,
        typed: typedFor(agreement, groups, values),
      }),
    [basePdf, signImageUrl, groups, values]
  );

  // Skip the update when there's nothing to clear, so an already-clean row doesn't trigger a re-render.
  const markBuildOk = useCallback((secret) => {
    setBuildErrors((prev) => {
      if (!(secret in prev)) return prev;
      const next = { ...prev };
      delete next[secret];
      return next;
    });
  }, []);

  const markBuildFailed = useCallback((agreement, error) => {
    setBuildErrors((prev) => ({ ...prev, [agreement.secret]: buildErrorFor(agreement, error) }));
  }, []);

  // This agreement can't be built for a reason the signer can't fix, so it's held and the rest are signed.
  const holdAgreement = useCallback(
    (agreement, error) => {
      markBuildOk(agreement.secret);
      setHeld((prev) => ({ ...prev, [agreement.secret]: buildErrorFor(agreement, error) }));
    },
    [markBuildOk]
  );

  // Going back off the marker entry leaves no stray entry behind; the popstate listener below does the closing.
  const closePreview = useCallback(() => {
    if (!onPreviewEntry()) {
      setOpenSecret(null);
    } else if (!leavingPreview.current) {
      // A second tap before that popstate would go back past the page.
      leavingPreview.current = true;
      window.history.back();
    }
  }, []);

  // A held agreement leaves the list it was opened from, so its preview closes with it.
  const holdAtPreview = useCallback(
    (agreement, error) => {
      holdAgreement(agreement, error);
      closePreview();
    },
    [holdAgreement, closePreview]
  );

  // Clears a closed row's stale error so the next pre-flight re-checks it; the open row's status comes from the effect below.
  useEffect(() => {
    setBuildErrors((prev) => {
      const next = {};
      for (const secret of Object.keys(prev)) {
        if (secret === openSecret) next[secret] = prev[secret];
      }
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [values, signImageUrl, openSecret]);

  useEffect(() => {
    const agreement = signable.find((item) => item.secret === openSecret);
    if (!agreement) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await pdfFor(agreement);
        if (cancelled) return;
        setPreview({ secret: agreement.secret, file: { data } });
        markBuildOk(agreement.secret);
      } catch (error) {
        if (cancelled) return;
        // Don't leave the previous, now-stale preview on screen under a fresh failure.
        setPreview((prev) => (prev?.secret === agreement.secret ? null : prev));
        sortPreviewFailure(agreement, error, { onHeld: holdAtPreview, onBuildFailed: markBuildFailed });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [openSecret, signable, pdfFor, markBuildOk, markBuildFailed, holdAtPreview]);

  const openPreview = (secret) => {
    // Back on a phone should close the overlay, not leave the page and what's typed; the entry keeps the router's state.
    if (!isWideScreen() && !onPreviewEntry()) window.history.pushState({ ...window.history.state, signAllPreview: true }, "");
    setOpenSecret(secret);
  };

  const previewOpen = openSecret !== null;
  useEffect(() => {
    if (!previewOpen) return undefined;
    const onPopState = () => {
      leavingPreview.current = false;
      if (!onPreviewEntry()) setOpenSecret(null);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [previewOpen]);

  // Closing removes the preview's Close, which may hold focus, so focus returns to the open row's toggle, or the row when that's disabled or held.
  useEffect(() => {
    const closed = shownSecret.current;
    shownSecret.current = openSecret;
    const row = closed && !openSecret ? rowRefs.current[closed] : null;
    if (row) (row.querySelector("button:not(:disabled)") ?? row).focus();
  }, [openSecret]);

  const onPreviewLoaded = useCallback((secret, numPages) => {
    setPageCounts((prev) => ({ ...prev, [secret]: numPages }));
    setOpened((prev) => ({ ...prev, [secret]: true }));
  }, []);

  // The result shows under the title, but the signer is usually scrolled down to the button they pressed.
  useEffect(() => {
    if (phase === "results") topRef.current?.scrollIntoView?.({ block: "start" });
  }, [phase]);

  // The success panel has no preview, so the phone overlay's entry goes too; left on top, the first Back would seem to do nothing.
  const allSigned = phase === "results" && !signable.some((agreement) => results[agreement.secret]?.status === "failed");
  useEffect(() => {
    if (allSigned && onPreviewEntry()) closePreview();
  }, [allSigned, closePreview]);

  // ENG-2015: a tab the portal flagged closes itself once everything in it is signed; anything held, blocked or not loaded, or a refused close, leaves it on the success panel.
  const closesTab = allSigned && unavailableCount === 0 && !agreements.some((agreement) => agreement.blocked);
  useEffect(() => {
    if (closesTab && closesOnSubmit(window.location.search)) window.close();
  }, [closesTab]);

  const onSignature = async (file) => {
    setSignatureUploading(true);
    let tried = null;
    try {
      const url = await uploadSignature(
        signable.map((a) => a.secret),
        (secret) => {
          tried = secret;
          return uploadFile("/upload", secret, file);
        }
      );
      setSignImageUrl(url);
      setSignatureOpen(false);
    } catch (error) {
      logFailure("signature upload", signable.find((a) => a.secret === tried)?.label ?? "the batch", error);
      showErrorToast("Your signature didn't upload. Please try again.");
    } finally {
      setSignatureUploading(false);
    }
  };

  const signOne = useCallback(
    async (agreement, freshLink) => {
      const pdf = await step("build", "Couldn't build the PDF", () => pdfFor(agreement));
      const finalDocumentUrl = await step("upload", "Couldn't upload the PDF", () =>
        uploadFile("/upload-sign-template-document", agreement.secret, new Blob([pdf], { type: "application/pdf" }))
      );
      await step("save", "Couldn't save the signature", async () => {
        const { url, body } = buildSignSubmit({
          serverBaseUrl,
          mode,
          signLink: freshLink,
          finalDocumentUrl,
          dateCreated: moment().format("YYYY-MM-DD"),
          dateSigned: moment().format("YYYY-MM-DD:HH:mm:ss"),
        });
        const response = await axios.post(url, body, { headers: secretHeader(agreement.secret) });
        if (!response?.data?.success) throw Object.assign(new Error("not saved"), { response });
      });
    },
    [pdfFor, mode]
  );

  const run = async (list) => {
    setPhase("signing");
    await signInOrder(list, { reload: load, signOne }, (secret, result) =>
      setResults((prev) => ({ ...prev, [secret]: result }))
    );
    setPhase("results");
  };

  const preparedForToday = () => {
    const next = refreshForToday(prepared, moment().format("MM/DD/YYYY"), (eligible, day) =>
      prepareAgreements(eligible, mode, day, USA_STATE_LIST)
    );
    if (next !== prepared) setPrepared(next);
    return next;
  };

  const handleSignAll = async () => {
    if (preflighting) return;
    setPreflighting(true);
    try {
      await signAfterPreflight(splitBlocked(withHeld(preparedForToday().agreements, held)).signable, {
        build: pdfFor,
        onBuilt: (agreement) => markBuildOk(agreement.secret),
        onBuildFailed: markBuildFailed,
        onHeld: holdAgreement,
        onBlocked: () => showErrorToast("Some agreements can't be built yet. See the message on each one."),
        run,
      });
    } finally {
      setPreflighting(false);
    }
  };

  const retryFailed = () =>
    run(splitBlocked(withHeld(preparedForToday().agreements, held)).signable.filter((agreement) => results[agreement.secret]?.status === "failed"));

  if (phase === "loading") {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }
  if (phase === "refused") {
    return (
      <Layout>
        <p className="p-10 text-center" data-testid="sign-all-refusal">
          {REFUSAL[refusal]}
        </p>
      </Layout>
    );
  }

  const count = signable.length;
  const labels = signable.map((agreement) => agreement.label);
  const failedCount = signable.filter((agreement) => results[agreement.secret]?.status === "failed").length;
  const signedCount = signable.filter((agreement) => results[agreement.secret]?.status === "signed").length;
  const blockedLines = [
    blockedText("missing", blockedMissing),
    blockedText("unavailable", blockedUnavailable),
    blockedText("details-unavailable", blockedDetails),
    ...heldGroups.map((group) => blockedText("build", group.labels, group.message)),
  ].filter(Boolean);
  const blockedNotes = blockedLines.length > 0 && (
    <div data-testid="sign-all-blocked">
      {blockedLines.map((line) => (
        <p key={line} className="text-sm text-gray-600">
          {line}
        </p>
      ))}
    </div>
  );
  const unavailable = unavailableText(unavailableCount);
  const reviewing = phase === "review";
  // Hoisted above the early returns below, because the all-blocked one also renders rows through renderRow.
  const editable = reviewing && !preflighting;

  // Z1: one row per prepared agreement, in load order; a blocked one carries its own warning and never an Open button.
  const renderRow = (agreement, index) => {
    const divider = index ? "border-t border-gray-200" : "";
    const rowRef = (element) => {
      rowRefs.current[agreement.secret] = element;
    };
    if (agreement.blocked) {
      return (
        <div
          key={agreement.secret}
          ref={rowRef}
          tabIndex={-1}
          data-testid="sign-all-blocked-row"
          aria-disabled="true"
          className={[divider, "bg-amber-50"].filter(Boolean).join(" ")}
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
            <span className="font-medium">{agreement.label}</span>
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm text-amber-800">
              <WarningIcon />
              <span className="min-w-0 break-words">{blockedRowText(agreement.blocked, agreement.heldMessage)}</span>
            </span>
          </div>
        </div>
      );
    }
    const isOpen = openSecret === agreement.secret;
    const status = rowStatus(results[agreement.secret], opened[agreement.secret], buildErrors[agreement.secret]);
    const highlight = isOpen ? "bg-violet-50 shadow-[inset_4px_0_0_0_#8b5cf6]" : "";
    return (
      <div
        key={agreement.secret}
        ref={rowRef}
        tabIndex={-1}
        className={[divider, highlight].filter(Boolean).join(" ")}
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <span className="flex-1 font-medium">{agreement.label}</span>
          <span className={`text-xs ${status.className}`}>{status.text}</span>
          <CustomButton
            size="sm"
            labelColor="black"
            label={<>{isOpen ? "Close" : "Open"}<span className="sr-only"> {agreement.label}</span></>}
            disabled={!editable}
            onClick={() => (isOpen ? closePreview() : openPreview(agreement.secret))}
          />
        </div>
      </div>
    );
  };

  if (count === 0) {
    return (
      <Layout>
        <div className="mx-auto w-full max-w-[680px] px-4 py-6 lg:max-w-[1200px]">
          <h2 className="text-xl font-semibold">{TITLE[mode]}</h2>
          {/* Z1: every eligible agreement is blocked, so the list holds only blocked rows, each with its own warning. */}
          <section className="mt-4 flex flex-col overflow-hidden rounded border border-gray-200 bg-white" data-testid="sign-all-agreements">
            {agreements.map((agreement, index) => renderRow(agreement, index))}
          </section>
          {/* Y4: what the signer sent isn't unsaid just because nothing here is left to sign. */}
          {alreadySignedLabels.length > 0 && (
            <p className="mt-3 text-sm text-gray-600">Already signed: {alreadySignedLabels.join(", ")}</p>
          )}
          {unavailable && <p className="mt-3 text-sm text-gray-600">{unavailable}</p>}
        </div>
      </Layout>
    );
  }

  if (phase === "results" && failedCount === 0) {
    return (
      <Layout>
        <div ref={topRef} className="mx-auto flex w-full max-w-[680px] flex-col gap-6 px-4 py-6 lg:max-w-[1200px]">
          <h2 className="text-xl font-semibold">{TITLE[mode]}</h2>
          <section
            role="status"
            data-testid="sign-all-success"
            className="flex flex-col items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-6 py-10 text-center"
          >
            <CheckIcon />
            <h3 className="text-2xl font-semibold text-green-900 sm:text-3xl">{successHeading(count)}</h3>
            <p className="text-green-900">{joinWords(labels)}</p>
            <p className="text-sm text-gray-700">{resultsText(0)}</p>
          </section>
          {/* What couldn't be loaded is still left to sign from the portal, so it stays said under the panel. */}
          {(blockedNotes || unavailable) && (
            <div>
              {blockedNotes}
              {unavailable && <p className="text-sm text-gray-600">{unavailable}</p>}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  const missing = missingSteps({
    unopenedLabels: signable.filter((agreement) => !opened[agreement.secret]).map((agreement) => agreement.label),
    detailsValid,
    erroredLabels: signable.filter((agreement) => buildErrors[agreement.secret]).map((agreement) => agreement.label),
    hasSignature: Boolean(signImageUrl),
    consent,
  });
  // Closing mid-pre-flight would clear its error marks; preflighting also spans the first signing pass, where closing is harmless.
  const closeLocked = preflighting && reviewing;
  const openAgreement = signable.find((agreement) => agreement.secret === openSecret) ?? null;

  return (
    <Layout>
      <div
        ref={topRef}
        className="mx-auto w-full max-w-[680px] px-4 py-6 lg:grid lg:max-w-[1200px] lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start lg:gap-6"
      >
        <div className="flex min-w-0 flex-col gap-6">
          <div>
            <h2 className="text-xl font-semibold">{TITLE[mode]}</h2>
            {phase === "results" && (
              <div
                role="alert"
                data-testid="sign-all-failed"
                className="my-3 flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1 text-red-900">
                  <p className="text-lg font-semibold">{signedSummary(signedCount, count)}</p>
                  <p className="text-sm">{resultsText(failedCount)}</p>
                </div>
                <CustomButton
                  labelColor="black"
                  label="Retry failed"
                  className="shrink-0 self-start whitespace-nowrap sm:self-auto"
                  onClick={() => retryFailed()}
                />
              </div>
            )}
            {/* In results the banner above already gives the signed count. */}
            {phase !== "results" && <p className="text-sm text-gray-600">{progressText(count)}</p>}
            {alreadySignedLabels.length > 0 && (
              <p className="text-sm text-gray-600">Already signed: {alreadySignedLabels.join(", ")}</p>
            )}
            {unavailable && <p className="text-sm text-gray-600">{unavailable}</p>}
            {/* Z1: a blocked agreement's warning now lives on its own row below, not as a line here. */}
          </div>

          {groups.length > 0 && (
            <section className="flex flex-col gap-3" data-testid="sign-all-details">
              <h3 className="text-sm text-gray-600">Your details, used in every agreement that asks for them</h3>
              {groups.map((group, index) => (
                <label key={group.key} htmlFor={`detail_${index}`} className="flex flex-col gap-1 text-sm">
                  {group.label}
                  <DetailInput
                    id={`detail_${index}`}
                    dataDetail={group.key.split("@")[0]}
                    input={group.input}
                    disabled={!editable}
                    value={Object.prototype.hasOwnProperty.call(values, group.key) ? values[group.key] : group.prefill}
                    onChange={(next) => setValues((prev) => ({ ...prev, [group.key]: next }))}
                  />
                </label>
              ))}
            </section>
          )}

          <section className="flex flex-col overflow-hidden rounded border border-gray-200 bg-white" data-testid="sign-all-agreements">
            {agreements.map((agreement, index) => renderRow(agreement, index))}
          </section>

          <section className="flex items-center gap-3">
            <div className="flex h-16 flex-1 items-center justify-center rounded border border-dashed border-gray-300 bg-white">
              {signImageUrl ? (
                <img src={signImageUrl} alt="Your signature" className="max-h-14" />
              ) : (
                <span className="text-sm text-gray-500">Your signature appears here</span>
              )}
            </div>
            <CustomButton
              labelColor="black"
              label={signImageUrl ? "Redraw signature" : "Draw signature"}
              disabled={!editable}
              onClick={() => setSignatureOpen(true)}
            />
          </section>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={consent}
              disabled={!editable}
              onChange={(e) => setConsent(e.target.checked)}
            />
            {consentText(labels)}
          </label>

          {reviewing && (
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 flex-1 text-sm text-gray-600">{continueText(missing)}</span>
              <CustomButton
                labelColor="black"
                label={signButtonLabel(count)}
                className="shrink-0 whitespace-nowrap"
                disabled={missing.length > 0 || signatureUploading}
                isLoading={preflighting}
                onClick={() => handleSignAll()}
              />
            </div>
          )}
          {phase === "signing" && <p className="text-sm text-gray-600">Signing… keep this page open.</p>}
        </div>
        <SignAllPreview
          agreement={openAgreement}
          preview={preview}
          pageCount={(openSecret && pageCounts[openSecret]) || 0}
          error={openSecret ? buildErrors[openSecret] : undefined}
          onLoaded={onPreviewLoaded}
          onClose={closePreview}
          closeDisabled={closeLocked}
        />
      </div>
      <SignatureModal
        open={signatureOpen && editable}
        onClose={() => setSignatureOpen(false)}
        onSubmit={(file) => onSignature(file)}
        signatureType=""
        defaultName={signable[0]?.signerName}
        isLoading={signatureUploading}
        operatorSign
      />
    </Layout>
  );
};

export default SignAll;
